import React, { JSX } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/free-regular-svg-icons";
import { BaseLogo } from "../../../../core/models/entities/BaseLogo";
import { getPendingUploadFile } from "../functions/uploadPending";

function formatSize(bytes: number | undefined): string {
  if (!bytes || !(bytes > 0)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The editor canvas of a worker-owned VECTOR upload (flag
 * REACT_APP_UPLOAD_PENDING_ENABLED, design 14/9 §5.3 / §9.1): pdf/ai/eps
 * cannot be painted by an <img> and the supplier has not rendered anything
 * yet, so the canvas shows a clear file card - name and size from the File
 * the user chose (pendingUploadFiles) or, after F5, the file name the API
 * stored - instead of an empty box or the sharp-wait skeleton. The
 * "Preparing logo…" badge in the toolbar says what is happening. Replaced
 * by the real <img> the moment the landing brings a bitmap. Rasterising the
 * vector locally (pdf.js, D2) is the documented next step.
 */
export function UploadFileCard({
  logo,
  insetForRulers,
}: {
  logo: BaseLogo;
  insetForRulers: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const held = getPendingUploadFile(logo.id);
  const name =
    held?.file.name ??
    (logo as unknown as { originalFileName?: string }).originalFileName ??
    logo.title;
  const size = formatSize(held?.file.size);
  return (
    <div
      className={
        "flex flex-col items-center justify-center gap-2 rounded border border-dashed border-gray-300 bg-white min-h-[325px] min-w-[450px] p-6 text-gray-600" +
        (insetForRulers ? " relative z-10" : "")
      }
      data-testid="upload-file-card"
      title={name}
    >
      <FontAwesomeIcon icon={faFile} size={"3x"} className={"opacity-50"} />
      <span className={"text-sm font-medium truncate max-w-[400px]"}>
        {name}
      </span>
      {size && <span className={"text-xs text-gray-500"}>{size}</span>}
      <span className={"text-xs italic text-gray-500"}>
        {t("logoEditorComponents.previewNotAvailableForFile")}
      </span>
    </div>
  );
}
